import React, { useEffect, useState } from "react";
import { Button, Input, message, Row, Col, Space } from "antd";
import {
    EditOutlined,
    PlusOutlined,
    DeleteOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { getUserRole } from "../../utils/auth";
import EmployeeDrawer from "./EmployeeDrawer";

export default function Employees() {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [paginationInfo, setPaginationInfo] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [userRole, setUserRole] = useState("");

    useEffect(() => {
        setUserRole(getUserRole());
    }, []);

    const getEmployees = async () => {
        try {
            setLoading(true);
            const { current, pageSize } = paginationInfo;
            const response = await API.get(`/employee/paginate`, {
                params: {
                    page: current,
                    limit: pageSize,
                    search: searchQuery,
                },
            });
            setEmployees(response.data.employees);
            setPaginationInfo((prev) => ({
                ...prev,
                total: response.data.pagination.totalEmployees,
            }));
        } catch (err) {
            console.error(err);
            message.error("Failed to fetch employees. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getEmployees();
    }, [paginationInfo.current, paginationInfo.pageSize, searchQuery]);

    const handleDeleteClick = (employee) => {
        setEmployeeToDelete(employee);
        setIsDeleteModalVisible(true);
    };

    const handleDeleteEmployee = async () => {
        try {
            await API.delete(`/employee/${employeeToDelete?._id}`);
            message.success("Employee deleted successfully!");
            getEmployees();
        } catch (err) {
            console.error("Error deleting employee:", err);
            message.error(err.response?.data?.message || "Failed to delete employee.");
        } finally {
            setIsDeleteModalVisible(false);
        }
    };

    const handleAddEmployee = async () => {
        setOpenDrawer(false);
        getEmployees();
    };

    const handleEditEmployee = async () => {
        setOpenDrawer(false);
        getEmployees();
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setPaginationInfo((prev) => ({ ...prev, current: 1 }));
    };

    const columns = [
        {
            title: "Index",
            key: "index",
            render: (_, __, index) =>
                (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Store",
            dataIndex: ["storeId","name"],
            key: "store",
        },
        {
            title: "Phone",
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: "Date of Joining",
            dataIndex: "doj",
            key: "doj",
            render: (doj) => new Date(doj).toLocaleDateString(),
        },
        {
            title: "Designation",
            dataIndex: "designation",
            key: "designation",
        },
        {
            title: "Salary",
            dataIndex: "salary",
            key: "salary",
            render: (salary) => {
                if (salary === undefined || salary === null || salary === '') {
                    return <span style={{ color: '#999', fontStyle: 'italic' }}>Not Set</span>;
                }
                return `₹${new Intl.NumberFormat().format(salary)}`;
            },
        },
        ...(userRole.editPermission
            ? [{
                title: "Actions",
                key: "actions",
                align: "center",
                render: (record) => (
                    <div>
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => {
                                setIsEdit(true);
                                setSelectedEmployee(record);
                                setOpenDrawer(true);
                            }}
                        />
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            style={{ marginLeft: 8 }}
                            onClick={() => handleDeleteClick(record)}
                        />
                    </div>
                ),
            }]
            : []),
    ];

    return (
        <div style={{
            padding: "20px",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }} justify="space-between">
                <Col xs={24} sm={12} md={12}>
                    <Space style={{ width: '100%' }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setOpenDrawer(true);
                                setIsEdit(false);
                                setSelectedEmployee(null);
                            }}
                            block
                        >
                            Add New
                        </Button>
                        <Input
                            placeholder="Search employees"
                            prefix={<SearchOutlined />}
                            value={searchQuery}
                            onChange={handleSearch}
                            style={{ width: "100%", minWidth: 30 }}
                        />
                    </Space>
                </Col>
            </Row>

            <EmployeeDrawer
                open={openDrawer}
                setOpen={setOpenDrawer}
                data={selectedEmployee}
                isEdit={isEdit}
                onSubmit={isEdit ? handleEditEmployee : handleAddEmployee}
            />

            <div style={{ overflowX: "auto" }}>
                <CustomTable
                    columns={columns}
                    dataSource={employees}
                    onPaginationChange={(pagination) => {
                        setPaginationInfo({
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                        });
                    }}
                    loading={loading}
                    total={paginationInfo.total}
                />
            </div>

            <ConfirmationModal
                visible={isDeleteModalVisible}
                onConfirm={handleDeleteEmployee}
                onCancel={() => setIsDeleteModalVisible(false)}
                title="Confirm Deletion"
                content="Are you sure you want to delete this employee? This action cannot be undone."
            />
        </div>
    );
}
